import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { QRCodeCanvas } from "qrcode.react";
import { ArrowLeft, PencilSimple, Printer, Package, Wrench, LinkSimple } from "@phosphor-icons/react";

function KV({ label, value, mono = false, testid }) {
  return (
    <div data-testid={testid}>
      <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500">{label}</div>
      <div className={`mt-1 text-sm text-zinc-100 ${mono ? "font-mono" : ""}`}>{value || <span className="text-zinc-600">—</span>}</div>
    </div>
  );
}

export default function InventoryDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [item, setItem] = useState(null);
  const [err, setErr] = useState("");
  const [logDate, setLogDate] = useState("");
  const [logNotes, setLogNotes] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get(`/inventory/${id}`);
      setItem(data);
    } catch (e) { setErr(formatApiError(e)); }
  };

  useEffect(() => { load(); }, [id]);

  const publicUrl = typeof window !== "undefined" ? `${window.location.origin}/item/${id}` : "";

  const addMaintenance = async () => {
    if (!logDate) return;
    try {
      await api.put(`/inventory/${id}`, {
        last_maintenance: logDate + "T00:00:00+00:00",
        maintenance_log_add: { date: logDate + "T00:00:00+00:00", notes: logNotes || null },
      });
      setLogDate(""); setLogNotes("");
      load();
    } catch (e) { setErr(formatApiError(e)); }
  };

  const printLabel = () => {
    window.location.href = `/labels?ids=${id}`;
  };

  if (err) return <div className="text-red-400">{err}</div>;
  if (!item) return <div className="text-zinc-500">Loading…</div>;

  const due = item.next_maintenance ? new Date(item.next_maintenance) : null;
  const overdue = due && due < new Date();

  return (
    <div className="space-y-6 animate-fade-up max-w-6xl mx-auto" data-testid="inventory-detail">
      <div className="flex items-center justify-between flex-wrap gap-3 no-print">
        <button onClick={() => nav("/inventory")} className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-[#F5A623]">
          <ArrowLeft size={14} /> Inventory
        </button>
        <div className="flex gap-2">
          <button onClick={printLabel} className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-zinc-900 border border-zinc-800 text-sm text-zinc-300 hover:text-white hover:bg-zinc-800 transition-colors" data-testid="print-label-btn">
            <Printer size={16} /> Print Label
          </button>
          <button onClick={() => nav(`/inventory/${id}/edit`)} className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-[#F5A623] hover:bg-[#E67E22] text-zinc-950 font-bold text-sm transition-colors" data-testid="edit-item-btn">
            <PencilSimple size={16} /> Edit
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: info */}
        <div className="lg:col-span-2 space-y-6">
          <section className="p-6 rounded-lg bg-zinc-900/50 border border-zinc-800 flex gap-6">
            <div className="h-32 w-32 md:h-40 md:w-40 rounded-md bg-zinc-800 overflow-hidden grid place-items-center flex-shrink-0 border border-zinc-700">
              {item.photo
                ? <img src={item.photo} alt={item.name} className="h-full w-full object-cover" />
                : <Package size={40} className="text-zinc-600" weight="duotone" />}
            </div>
            <div className="min-w-0 flex-1 space-y-2">
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#F5A623]">{item.category}</div>
              <h1 className="text-3xl sm:text-4xl font-bold tracking-tight" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
                {item.name}
              </h1>
              <div className="text-sm text-zinc-400 font-mono">{item.sku}</div>
              <div className="flex flex-wrap gap-2 pt-2">
                <span className="inline-block px-2 py-1 rounded-sm text-xs bg-zinc-800 border border-zinc-700 text-zinc-300">Qty · {item.quantity}</span>
                <span className="inline-block px-2 py-1 rounded-sm text-xs bg-zinc-800 border border-zinc-700 text-zinc-300">{item.location}</span>
                {item.floor && <span className="inline-block px-2 py-1 rounded-sm text-xs bg-zinc-800 border border-zinc-700 text-zinc-300">{item.floor}</span>}
                {due && (
                  <span className={`inline-block px-2 py-1 rounded-sm text-xs font-mono border ${overdue ? "bg-red-500/10 text-red-400 border-red-900/50" : "bg-[#F5A623]/10 text-[#F5A623] border-[#F5A623]/20"}`}>
                    Next maint: {due.toLocaleDateString()}
                  </span>
                )}
              </div>
            </div>
          </section>

          <section className="p-6 rounded-lg bg-zinc-900/50 border border-zinc-800 grid grid-cols-2 sm:grid-cols-3 gap-5">
            <KV label="Serial Number" value={item.serial_number} mono testid="kv-serial" />
            <KV label="IP Address" value={item.ip_address} mono testid="kv-ip" />
            <KV label="Purchase Date" value={item.purchase_date ? new Date(item.purchase_date).toLocaleDateString() : null} mono testid="kv-purchase-date" />
            <KV label="Purchase Price" value={item.purchase_price != null ? `$${Number(item.purchase_price).toFixed(2)}` : null} mono testid="kv-purchase-price" />
            <KV label="Vendor" value={item.purchase_vendor} testid="kv-vendor" />
            <KV label="End of Life" value={item.end_of_life ? new Date(item.end_of_life).toLocaleDateString() : null} mono testid="kv-eol" />
            <KV label="Last Maintenance" value={item.last_maintenance ? new Date(item.last_maintenance).toLocaleDateString() : null} mono testid="kv-last-maintenance" />
            <KV label="Next Maintenance" value={item.next_maintenance ? new Date(item.next_maintenance).toLocaleDateString() : null} mono testid="kv-next-maintenance" />
            <KV label="Interval (days)" value={item.maintenance_interval_days} mono testid="kv-interval" />
            <KV label="Registered On" value={new Date(item.created_at).toLocaleString()} mono testid="kv-created" />
          </section>

          {item.notes && (
            <section className="p-6 rounded-lg bg-zinc-900/50 border border-zinc-800">
              <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 mb-2">Notes</div>
              <p className="text-sm text-zinc-300 whitespace-pre-wrap">{item.notes}</p>
            </section>
          )}

          <section className="p-6 rounded-lg bg-zinc-900/50 border border-zinc-800">
            <div className="flex items-center justify-between mb-4">
              <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 flex items-center gap-2"><Wrench size={14} /> Maintenance Log</div>
            </div>
            <div className="space-y-3 mb-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <input type="date" value={logDate} onChange={(e) => setLogDate(e.target.value)} data-testid="maintenance-date-input" className="px-3 py-2 bg-zinc-950 border border-zinc-800 rounded-md text-sm" />
                <input value={logNotes} onChange={(e) => setLogNotes(e.target.value)} placeholder="Notes (optional)" data-testid="maintenance-notes-input" className="sm:col-span-2 px-3 py-2 bg-zinc-950 border border-zinc-800 rounded-md text-sm" />
              </div>
              <button onClick={addMaintenance} data-testid="add-maintenance-btn" className="px-3 py-2 rounded-md bg-[#F5A623] text-zinc-950 text-xs font-bold hover:bg-[#E67E22]">
                Log Maintenance
              </button>
            </div>
            <div className="divide-y divide-zinc-800/60">
              {(item.maintenance_log || []).slice().reverse().map((m, idx) => (
                <div key={idx} className="py-2 text-sm flex justify-between">
                  <span className="text-zinc-300">{m.notes || "Scheduled maintenance"}</span>
                  <span className="font-mono text-xs text-zinc-500">{new Date(m.date).toLocaleDateString()}</span>
                </div>
              ))}
              {(!item.maintenance_log || item.maintenance_log.length === 0) && (
                <div className="text-sm text-zinc-600 py-3">No maintenance logged yet.</div>
              )}
            </div>
          </section>
        </div>

        {/* Right: QR */}
        <aside className="lg:col-span-1">
          <div className="p-6 rounded-lg bg-zinc-900/50 border border-zinc-800 space-y-4 sticky top-20">
            <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500">Asset QR Code</div>
            <div className="bg-white rounded-md p-4 grid place-items-center">
              <QRCodeCanvas value={publicUrl} size={200} fgColor="#09090B" bgColor="#FAFAFA" level="H" includeMargin={false} />
            </div>
            <div className="text-[11px] text-zinc-500 font-mono break-all" data-testid="qr-url">{publicUrl}</div>
            <div className="flex items-center gap-2 text-xs">
              <LinkSimple size={14} className="text-[#F5A623]" />
              <button onClick={() => { navigator.clipboard?.writeText(publicUrl); }} className="text-zinc-400 hover:text-[#F5A623] text-xs" data-testid="copy-qr-url">Copy link</button>
            </div>
            <p className="text-xs text-zinc-500 leading-relaxed">
              Scan with any camera. Anyone with the code can view asset history, purchase info, IP, and maintenance — without logging in.
            </p>
            <button onClick={printLabel} data-testid="print-qr-label-btn" className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-md bg-zinc-800 border border-zinc-700 text-sm text-zinc-300 hover:text-white hover:bg-zinc-700 transition-colors">
              <Printer size={14} /> Print Label
            </button>
          </div>
        </aside>
      </div>
    </div>
  );
}
